<script setup lang="ts">
import config from "@/assets/asset-config.json";
import type {
  IAssetConfig,
  DefinitionConfig,
  CategoryConfig,
  DefinitionRecord,
} from "@/types/definiation-config";
import { Animations, Categorey, Sex } from "@/enums/characterGeneratorEnums";
import { computed, ComputedRef, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  SelectedDefinition,
  SelectedItems,
  SheetDefinition,
} from "@/types/sheet-definiation";
import { showConfirmDialog } from "vant";
import { onMounted } from "vue";
import { CharacterStagePreviewInstance } from "./CharacterStagePreview.vue";

export interface CharacterGeneratorInstance {
  exportCharacterGif: typeof exportCharacterGif;
}

// Constants
const animationFilter: Animations[] = [];
const AssetConfig = config as IAssetConfig[];
const LEVEL_1_CATEGORIES_MAP: Record<Categorey, IAssetConfig> = AssetConfig.map(
  (item) => item
).reduce((acc, curr) => {
  acc[curr.label] = curr;
  return acc;
}, {} as Record<string, IAssetConfig>);

// 通过asset-config.json获取默认角色项
const getDefaultCharacterItems = (selectedSex: Sex): SelectedItems => {
  const shadow: SheetDefinition = (
    (config[0] as CategoryConfig).children![0] as DefinitionConfig
  ).items[0].definition;
  const body: SheetDefinition = (
    (config[0] as CategoryConfig).children![1] as DefinitionConfig
  ).items[0].definition;
  const headIdx = {
    [Sex.Male]: 2,
    [Sex.Female]: 1,
    [Sex.Child]: 0,
    [Sex.Teen]: 1,
    [Sex.Pregnant]: 1,
    [Sex.Muscular]: 2,
  };
  const head: SheetDefinition = (
    (config[1] as CategoryConfig).children![0] as DefinitionConfig
  ).items[headIdx[selectedSex]].definition;

  return {
    [Categorey.Body]: {
      Shadow: {
        ...shadow,
        selectedVariant: 0,
      } as SelectedDefinition,
      Bodies: {
        ...body,
        selectedVariant: 0,
      } as SelectedDefinition,
    },
    [Categorey.Head]: {
      Heads: {
        ...head,
        selectedVariant: 0,
      } as SelectedDefinition,
    },
  } as unknown as SelectedItems;
};

// 获取随机角色配置
const getRandomCharacterItems = (
  selectedSex: Sex,
  animationFilter: Animations[] = []
): SelectedItems => {
  // 先获取默认配置，确保基础部件存在
  const defaultItems = getDefaultCharacterItems(selectedSex);
  const result: Record<
    Categorey,
    Record<string, SelectedDefinition>
  > = JSON.parse(JSON.stringify(defaultItems)) as Record<
    Categorey,
    Record<string, SelectedDefinition>
  >;

  // 必需的部件类型
  const requiredItems = {
    clothes: false, // 衣服
    legs: false, // 裤子
    hair: false, // 头发
  };

  // 符合条件的部件集合
  const validItemsByType: Record<
    string,
    Array<{ definition: SheetDefinition; categoryKey: string }>
  > = {
    clothes: [],
    legs: [],
    hair: [],
  };

  // 检查动画是否符合过滤条件
  const checkAnimationFilter = (definition: SheetDefinition): boolean => {
    // 跳过自定义动画
    if (definition["layer_1"].custom_animation) {
      return false;
    }

    // 如果没有设置动画过滤，则不过滤
    if (animationFilter.length === 0) {
      return true;
    }

    // 如果definition没有animations属性或为空，则过滤掉
    if (
      !definition.animations ||
      !Array.isArray(definition.animations) ||
      definition.animations.length === 0
    ) {
      return false;
    }

    // 检查definition.animations是否包含所有animationFilter中的元素
    return animationFilter.every((anim) =>
      definition.animations?.includes(anim)
    );
  };

  // 遍历所有类别
  Object.entries(LEVEL_1_CATEGORIES_MAP).forEach(
    ([categoryKey, categoryValue]) => {
      const category = categoryKey as Categorey;
      if (!result[category]) {
        result[category] = {};
      }

      // 收集符合条件的部件
      const collectValidItems = (
        catConfig: IAssetConfig,
        categoryName: string
      ) => {
        // 如果是顶级类别
        if (catConfig.type === "category" && catConfig.children) {
          // 遍历所有子类别
          catConfig.children.forEach((child) => {
            collectValidItems(child, categoryName);
          });
        }
        // 如果是定义类别
        else if (catConfig.type === "definition") {
          const defConfig = catConfig as DefinitionConfig;
          // 过滤出适合当前性别的项目
          defConfig.items.forEach((item) => {
            if (!item.definition) return;

            // 检查是否符合动画过滤条件
            if (!checkAnimationFilter(item.definition)) return;

            // 检查是否有适用于当前性别的图层
            const layers = Object.keys(item.definition).filter((key) =>
              key.startsWith("layer_")
            );
            const hasSexLayer = layers.some((layerKey) => {
              const layer = item.definition[layerKey] as unknown as Record<
                string,
                unknown
              >;
              return layer && layer[selectedSex.toLowerCase()];
            });

            if (hasSexLayer) {
              // 根据type_name分类存储有效项目
              if (item.definition.type_name === "clothes") {
                validItemsByType.clothes.push({
                  definition: item.definition,
                  categoryKey: categoryName,
                });
              } else if (item.definition.type_name === "legs") {
                validItemsByType.legs.push({
                  definition: item.definition,
                  categoryKey: categoryName,
                });
              } else if (item.definition.type_name === "hair") {
                validItemsByType.hair.push({
                  definition: item.definition,
                  categoryKey: categoryName,
                });
              }
            }
          });
        }
      };

      // 收集所有有效部件
      collectValidItems(categoryValue, categoryKey);

      // 处理各级配置进行随机化
      const processCategory = (catConfig: IAssetConfig) => {
        // 如果是顶级类别
        if (catConfig.type === "category" && catConfig.children) {
          // 随机选择一个子类别处理
          const randomChildIndex = Math.floor(
            Math.random() * catConfig.children.length
          );
          const randomChild = catConfig.children[randomChildIndex];
          processCategory(randomChild);
        }
        // 如果是定义类别
        else if (catConfig.type === "definition") {
          const defConfig = catConfig as DefinitionConfig;
          // 过滤出适合当前性别的项目并且符合动画过滤条件的项目
          const validItems = defConfig.items.filter((item) => {
            if (!item.definition) return false;

            // 检查是否符合动画过滤条件
            if (!checkAnimationFilter(item.definition)) return false;

            // 检查是否有适用于当前性别的图层
            const layers = Object.keys(item.definition).filter((key) =>
              key.startsWith("layer_")
            );
            return layers.some((layerKey) => {
              const layer = item.definition[layerKey] as unknown as Record<
                string,
                unknown
              >;
              return layer && layer[selectedSex.toLowerCase()];
            });
          });

          // 如果有有效项目，随机选择一个
          if (validItems.length > 0) {
            const randomItemIndex = Math.floor(
              Math.random() * validItems.length
            );
            const randomItem = validItems[randomItemIndex];
            const definition = randomItem.definition;

            // 检查并记录必需的部件类型
            if (definition.type_name === "clothes") {
              requiredItems.clothes = true;
            } else if (definition.type_name === "legs") {
              requiredItems.legs = true;
            } else if (definition.type_name === "hair") {
              requiredItems.hair = true;
            }

            // 随机选择变体
            let randomVariantIndex = 0;
            if (definition.variants && definition.variants.length > 0) {
              randomVariantIndex = Math.floor(
                Math.random() * definition.variants.length
              );
            }

            // 添加到结果
            result[category][definition.name] = {
              ...definition,
              selectedVariant: randomVariantIndex,
            } as SelectedDefinition;
          }
        }
      };

      // 只对非基础部件进行随机化处理
      // 身体和头部的基础部件已通过默认配置保证存在
      if (category !== Categorey.Body && category !== Categorey.Head) {
        processCategory(categoryValue);
      }
    }
  );

  // 确保必需部件存在
  // 添加衣服
  if (!requiredItems.clothes && validItemsByType.clothes.length > 0) {
    const randomClothes =
      validItemsByType.clothes[
        Math.floor(Math.random() * validItemsByType.clothes.length)
      ];
    const definition = randomClothes.definition;
    let randomVariantIndex = 0;
    if (definition.variants && definition.variants.length > 0) {
      randomVariantIndex = Math.floor(
        Math.random() * definition.variants.length
      );
    }

    const categoryKey = randomClothes.categoryKey as Categorey;
    if (!result[categoryKey]) {
      result[categoryKey] = {};
    }

    result[categoryKey][definition.name] = {
      ...definition,
      selectedVariant: randomVariantIndex,
    } as SelectedDefinition;
  }

  // 添加裤子
  if (!requiredItems.legs && validItemsByType.legs.length > 0) {
    const randomLegs =
      validItemsByType.legs[
        Math.floor(Math.random() * validItemsByType.legs.length)
      ];
    const definition = randomLegs.definition;
    let randomVariantIndex = 0;
    if (definition.variants && definition.variants.length > 0) {
      randomVariantIndex = Math.floor(
        Math.random() * definition.variants.length
      );
    }

    const categoryKey = randomLegs.categoryKey as Categorey;
    if (!result[categoryKey]) {
      result[categoryKey] = {};
    }

    result[categoryKey][definition.name] = {
      ...definition,
      selectedVariant: randomVariantIndex,
    } as SelectedDefinition;
  }

  // 添加头发
  if (!requiredItems.hair && validItemsByType.hair.length > 0) {
    const randomHair =
      validItemsByType.hair[
        Math.floor(Math.random() * validItemsByType.hair.length)
      ];
    const definition = randomHair.definition;
    let randomVariantIndex = 0;
    if (definition.variants && definition.variants.length > 0) {
      randomVariantIndex = Math.floor(
        Math.random() * definition.variants.length
      );
    }

    const categoryKey = randomHair.categoryKey as Categorey;
    if (!result[categoryKey]) {
      result[categoryKey] = {};
    }

    result[categoryKey][definition.name] = {
      ...definition,
      selectedVariant: randomVariantIndex,
    } as SelectedDefinition;
  }

  return result as unknown as SelectedItems;
};

// Props & Emits
const props = withDefaults(
  defineProps<{
    name?: string;
    selectedSex: Sex;
    selectedItems: SelectedItems;
    showNameEdit?: boolean;
  }>(),
  {
    selectedSex: Sex.Male,
    selectedItems: () => ({} as SelectedItems),
    showNameEdit: true,
  }
);

const emit = defineEmits<{
  (e: "update:name", name: string): void;
  (e: "update:selectedSex", sex: Sex): void;
  (e: "update:selectedItems", items: SelectedItems): void;
}>();

// Lifecycle
onMounted(() => {
  if (Object.keys(props.selectedItems).length === 0) {
    emit("update:selectedItems", getDefaultCharacterItems(props.selectedSex));
  }
});

watch(
  () => props.selectedItems,
  (items: SelectedItems) => {
    if (Object.keys(items).length === 0) {
      emit("update:selectedItems", getDefaultCharacterItems(props.selectedSex));
    }
  }
);
// States
const { t } = useI18n();
const characterStagePreviewRef = ref<CharacterStagePreviewInstance | null>(
  null
);
const sex_list: ComputedRef<
  {
    text: string;
    value: Sex;
    hide?: boolean;
  }[]
> = computed(() => [
  {
    text: t("characterGenerator.sexes.male"),
    value: Sex.Male,
  },
  {
    text: t("characterGenerator.sexes.female"),
    value: Sex.Female,
  },
  {
    text: t("characterGenerator.sexes.child"),
    value: Sex.Child,
  },
  {
    text: t("characterGenerator.sexes.teen"),
    value: Sex.Teen,
  },
  {
    text: t("characterGenerator.sexes.pregnant"),
    value: Sex.Pregnant,
    hide: true,
  },
  {
    text: t("characterGenerator.sexes.muscular"),
    value: Sex.Muscular,
  },
]);

const selectedCategory = ref<Categorey>(Categorey.Body);
const showSexSelectionPopup = ref(false);
const focusingDefinition = ref<{
  definition?: DefinitionRecord;
  type?: string;
  valid: boolean;
}>({
  valid: false,
});

const onSexChange = async (sex: Sex) => {
  try {
    await showConfirmDialog({
      message: t("characterGenerator.messages.resetInfo"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    emit("update:selectedSex", sex);
    emit("update:selectedItems", getDefaultCharacterItems(sex));
  } catch (e) {
    // ignore
  }
};

// 随机生成角色
const randomizeCharacter = async () => {
  emit(
    "update:selectedItems",
    getRandomCharacterItems(props.selectedSex, animationFilter)
  );
};

// 导出GIF的方法
const exportCharacterGif = (
  ...args: Parameters<CharacterStagePreviewInstance["exportToGif"]>
) => {
  if (characterStagePreviewRef.value) {
    characterStagePreviewRef.value.exportToGif(...args);
  } else {
    console.warn("Character preview component not available");
  }
};

const isSelected = (
  def: SheetDefinition,
  type: string,
  variantIdx?: number
) => {
  const target = props.selectedItems[selectedCategory.value]?.[type];
  if (variantIdx !== undefined) {
    return target?.name === def.name && target?.selectedVariant === variantIdx;
  }
  return target?.name === def.name;
};

const onSelectSheetDefinition = (
  def: SheetDefinition,
  type: string,
  variantIdx: number = 0
) => {
  const old = props.selectedItems[selectedCategory.value]?.[type];
  const isSame =
    old && old.name === def.name && old.selectedVariant === variantIdx;
  if (isSame) {
    emit("update:selectedItems", {
      ...props.selectedItems,
      [selectedCategory.value]: {
        ...(props.selectedItems[selectedCategory.value] ?? {}),
        [type]: {},
      },
    });
  } else {
    emit("update:selectedItems", {
      ...props.selectedItems,
      [selectedCategory.value]: {
        ...(props.selectedItems[selectedCategory.value] ?? {}),
        [type]: {
          ...def,
          selectedVariant: variantIdx,
        },
      },
    });
  }
};

// 导出方法供父组件使用
defineExpose({
  exportCharacterGif,
});
</script>
<template>
  <div class="grid grid-rows-[1fr_1fr] h-full overflow-hidden relative">
    <div class="grid grid-cols-2 p-2 gap-2">
      <!-- Preview -->
      <div
        class="bg-base-200 rounded-lg grid grid-rows-[auto_1fr] overflow-hidden w-full"
      >
        <div class="mt-2 px-2">
          <input
            v-if="showNameEdit"
            class="input input-sm input-bordered rounded w-full"
            :class="{
              'input-primary': !name,
            }"
            :placeholder="t('characterGenerator.messages.nameThisFigure')"
            :value="name"
            @input="
              emit('update:name', ($event.target as HTMLInputElement).value)
            "
          />
        </div>
        <CharacterStagePreview
          ref="characterStagePreviewRef"
          :selected-items="selectedItems"
          :selected-sex="selectedSex"
          class="pt-2"
        />
      </div>
      <!-- Selected Items -->
      <div class="bg-base-200 rounded-lg h-full">
        <SelectedItemGrid
          :selected-sex="selectedSex"
          :selected-items="selectedItems"
          @update:selectedItems="emit('update:selectedItems', $event)"
        />
      </div>
    </div>
    <div
      class="overflow-hidden h-full grid grid-rows-[auto_auto_1fr] border rounded-tl-lg rounded-tr-lg pt-4 shadow bg-base-200 mb-4 mx-2"
    >
      <div class="flex items-center justify-between px-2 mb-2">
        <div class="flex items-center join">
          <div
            class="join-item btn btn-outline btn-xs rounded pointer-events-none"
          >
            {{ $t("characterGenerator.bodyType") }}
          </div>
          <button
            class="join-item btn btn-xs btn-outline w-fit rounded"
            @click="showSexSelectionPopup = true"
          >
            {{ $t(`characterGenerator.sexes.${selectedSex}`) }}
            <i class="iconify mingcute--down-small-line" />
          </button>
        </div>
        <button
          class="btn btn-xs btn-outline rounded"
          @click="randomizeCharacter"
        >
          <i class="iconify mingcute--shuffle-line mr-1" />
          {{ $t("trait.random") }}
        </button>
      </div>
      <div class="tabs w-full overflow-x-auto px-2">
        <div>
          <a
            v-for="(item, key) in LEVEL_1_CATEGORIES_MAP"
            :key="key"
            class="tab tab-lifted tab-xs"
            :class="{ 'tab-active': key === selectedCategory }"
            @click="selectedCategory = key"
          >
            {{ $t(`characterGenerator.itemName.${item.label}`) }}
          </a>
        </div>
      </div>
      <div class="overflow-y-auto p-2 bg-base-100">
        <CharacterItemsGrid
          :animation-filter="animationFilter"
          :selectedSex="selectedSex"
          :selectedCategory="selectedCategory"
          :selectedItems="selectedItems"
          @update:selectedItems="emit('update:selectedItems', $event)"
          @update:focusingDefinition="focusingDefinition = $event"
        />
      </div>
    </div>
    <ModalSelectOption
      v-model:show="showSexSelectionPopup"
      :initial-value="selectedSex"
      :options="sex_list.filter((item) => !item.hide)"
      @confirm="onSexChange"
    />
    <VanPopup
      :show="focusingDefinition.valid"
      @update:show="focusingDefinition.valid = false"
      position="bottom"
      round
      class="h-[50%] grid grid-rows-[auto_1fr] border bg-base-200 shadow-lg"
      overlay-class="bg-transparent"
    >
      <ThreadsNav
        :show-back="true"
        :go-back="() => (focusingDefinition.valid = false)"
      >
        <template #center>
          <div class="flex justify-center items-center p-4">
            {{
              $t(
                `characterGenerator.itemDetailName.${focusingDefinition.definition?.definition?.name}`
              )
            }}
          </div>
        </template>
      </ThreadsNav>
      <div class="overflow-y-auto p-2 content-start flex flex-col gap-2">
        <div class="grid grid-cols-5 gap-2">
          <DefiniationItem
            v-for="(variant, idx) in focusingDefinition.definition?.definition
              ?.variants"
            :key="`${focusingDefinition.definition?.definition?.name}-${selectedSex}-${variant}`"
            :definition="focusingDefinition.definition!.definition"
            :is-selected="isSelected(focusingDefinition.definition!.definition, focusingDefinition.type!, idx)"
            :selected-sex="selectedSex"
            :variant-idx="idx"
            @click="
              onSelectSheetDefinition(
                focusingDefinition.definition!.definition,
                focusingDefinition.type!,
                idx
              )
            "
          />
        </div>
      </div>
    </VanPopup>
  </div>
</template>
